import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import UpgradeOutlinedIcon from "@mui/icons-material/UpgradeOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Title from "../components/PageTitle";
import useIsMobile from "../Hooks/useIsMobile";

// ------------------- EVENTS ENUM ---------------------
const EVENT_OPTIONS = [
  "SUBSCRIPTION_CREATED",
  "SUBSCRIPTION_RENEWED",
  "SUBSCRIPTION_PAUSED",
  "SUBSCRIPTION_BACK_TO_ACTIVE",
  "INSTALLMENT_PAID",
  "SUBSCRIPTION_EXTENDED",
  "PLAN_CHANGED",
  "PRICE_OVERRIDDEN",
  "QUOTA_OVERRIDDEN",
  "ONE_OFF_GRANT_APPLIED",
  "TEMP_UPGRADE_APPLIED",
  "TEMP_UPGRADE_EXPIRED",
  "RESOURCE_PERIOD_RESET",
  "PAYMENT_CREATED",
  "SUBSCRIPTION_AUTO_EXPIRED",
  "SUBSCRIPTION_AUTO_PAUSED",
];

// ------------------- EVENT DISPLAY META ---------------------
// Color + icon per event so the log feed is scannable at a glance instead of
// every entry looking identical.
const EVENT_META = {
  SUBSCRIPTION_CREATED: { color: "#4CAF50", Icon: CheckCircleOutlineIcon },
  SUBSCRIPTION_RENEWED: { color: "#4CAF50", Icon: RestartAltOutlinedIcon },
  SUBSCRIPTION_RENEWED_PARTIAL: { color: "#4CAF50", Icon: RestartAltOutlinedIcon },
  SUBSCRIPTION_BACK_TO_ACTIVE: { color: "#4CAF50", Icon: PlayCircleOutlineIcon },
  SUBSCRIPTION_PAUSED: { color: "#FF9800", Icon: PauseCircleOutlineIcon },
  SUBSCRIPTION_EXTENDED: { color: "#2196F3", Icon: EventRepeatOutlinedIcon },
  PLAN_CHANGED: { color: "#7C4DFF", Icon: SwapHorizOutlinedIcon },
  PRICE_OVERRIDDEN: { color: "#FF9800", Icon: PriceChangeOutlinedIcon },
  QUOTA_OVERRIDDEN: { color: "#FF9800", Icon: TuneOutlinedIcon },
  ONE_OFF_GRANT_APPLIED: { color: "#009688", Icon: BoltOutlinedIcon },
  TEMP_UPGRADE_APPLIED: { color: "#7C4DFF", Icon: UpgradeOutlinedIcon },
  TEMP_UPGRADE_EXPIRED: { color: "#F44336", Icon: HourglassEmptyOutlinedIcon },
  RESOURCE_PERIOD_RESET: { color: "#2196F3", Icon: RestartAltOutlinedIcon },
  INSTALLMENT_PAID: { color: "#4CAF50", Icon: PaymentsOutlinedIcon },
  PAYMENT_CREATED: { color: "#4CAF50", Icon: PaymentsOutlinedIcon },
  SUBSCRIPTION_AUTO_EXPIRED: { color: "#F44336", Icon: HourglassEmptyOutlinedIcon },
  SUBSCRIPTION_AUTO_PAUSED: { color: "#FF9800", Icon: PauseCircleOutlineIcon },
};
const DEFAULT_EVENT_META = { color: "#757575", Icon: HistoryIcon };

// keys whose numeric value reads better with thousands separators (prices,
// charges, credits) — day counts / percentages stay as plain numbers.
const MONEY_KEY_PATTERN = /price|amount|value|collect|refund|paid|due|charged|overpaid/i;

const prettifyKey = (key) => key.replace(/_/g, " ");

const formatNumber = (key, value) =>
  MONEY_KEY_PATTERN.test(key) ? value.toLocaleString("en-IN") : value.toString();

const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const SubscriptionLogs = () => {
  const isMobile = useIsMobile();
  const { subId } = useParams();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const theme = useTheme();
  const subscriptionId = location.state?.subscriptionId || subId;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [eventFilter, setEventFilter] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(7);
  const [totalPages, setTotalPages] = useState(1);

  const formatDate = (value) => {
    const date = new Date(value);
    if (!isNaN(date)) {
      return date.toLocaleDateString("en-GB").replace(/\//g, "-");
    }
    return null;
  };

  // Renders one leaf (string/number/boolean), formatting dates and
  // money-like numbers along the way.
  const renderLeaf = (key, value) => {
    if (typeof value === "string") {
      const d = formatDate(value);
      return d || value || "—";
    }
    if (typeof value === "number") {
      return formatNumber(key, value);
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value?.toString() ?? "—";
  };

  // Recursively renders a payload object as label/value rows, grouping
  // nested objects into their own visually distinct sub-section instead of
  // one flat, uniformly-indented dump.
  const renderFields = (obj, depth = 0) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: depth === 0 ? 1 : 0.75 }}>
      {Object.entries(obj).map(([k, v]) => {
        if (Array.isArray(v)) {
          return (
            <Box key={k} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                {prettifyKey(k)}
              </Typography>
              {v.length === 0 ? (
                <Typography variant="body2">—</Typography>
              ) : (
                v.map((item, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={typeof item === "object" ? JSON.stringify(item) : renderLeaf(k, item)}
                    sx={{ borderRadius: "6px" }}
                  />
                ))
              )}
            </Box>
          );
        }

        if (typeof v === "object" && v !== null) {
          return (
            <Box
              key={k}
              sx={{
                p: 1.5,
                mt: 0.5,
                borderRadius: 2,
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? `rgba(255,255,255,${0.03 + depth * 0.02})`
                    : `rgba(0,0,0,${0.02 + depth * 0.015})`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: "text.secondary" }}
              >
                {prettifyKey(k)}
              </Typography>
              <Box sx={{ mt: 0.75 }}>{renderFields(v, depth + 1)}</Box>
            </Box>
          );
        }

        return (
          <Box key={k} sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>
              {prettifyKey(k)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {renderLeaf(k, v)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  useEffect(() => {
    fetchLogs();
  }, [subscriptionId, page, debouncedSearch, eventFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);

      const url = `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/ledger/subscription/${subscriptionId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          search: debouncedSearch || "",
          event: eventFilter || "",
        },
      });

      setLogs(res.data?.data || []);
      setTotalPages(res.data?.totalPages || 1);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setError("Failed to load logs");
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: isMobile ? 1 : 3 }}>
      <Title icon={<HistoryIcon />} text={"Logs"} />

      {/* FILTERS */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Search Bar */}
        <Box sx={{ flexGrow: 1, minWidth: isMobile ? "100%" : "65%" }}>
          <TextField
            placeholder="Search reason, event..."
            variant="outlined"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#333" : "#f9f9f9",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Event Filter Dropdown */}
        <Box sx={{ minWidth: isMobile ? "100%" : "250px" }}>
          <TextField
            select
            label="Event"
            value={eventFilter}
            onChange={(e) => {
              setPage(1);
              setEventFilter(e.target.value);
            }}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          >
            <MenuItem value="">All Events</MenuItem>
            {EVENT_OPTIONS.map((ev) => (
              <MenuItem key={ev} value={ev}>
                {ev}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Fetching logs...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {/* Empty */}
      {!loading && logs.length === 0 && (
        <Typography sx={{ mt: 2 }} color="text.secondary">
          No logs found.
        </Typography>
      )}

      {/* Logs */}
      {!loading &&
        logs.length > 0 &&
        logs.map((log) => {
          const { color, Icon } = EVENT_META[log.event] || DEFAULT_EVENT_META;
          // A literal "/" placeholder reason isn't a real reason — don't show it.
          const hasReason = log.reason && log.reason.trim() && log.reason.trim() !== "/";

          return (
            <Card
              key={log._id}
              sx={{
                mt: 2,
                borderRadius: 3,
                borderLeft: `4px solid ${color}`,
                border: "1px solid #eaeaea",
                borderLeftWidth: 4,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.15s ease",
                "&:hover": {
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: hasReason ? 1 : 2,
                  }}
                >
                  <Chip
                    icon={<Icon sx={{ color: `${color} !important` }} />}
                    label={prettifyKey(log.event)}
                    sx={{
                      fontWeight: 700,
                      color,
                      backgroundColor: `${color}1a`,
                      border: `1px solid ${color}55`,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(log.createdAt)}
                  </Typography>
                </Box>

                {hasReason && (
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: "italic" }} color="text.secondary">
                    “{log.reason}”
                  </Typography>
                )}

                {log.payload && Object.keys(log.payload).length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      mt: 1,
                      borderRadius: 2,
                      border: "1px solid #e4e7eb",
                    }}
                  >
                    {renderFields(log.payload)}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}

      {!loading && totalPages > 1 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            py: 2,
            backgroundColor: theme.palette.mode === "dark" ? "#333" : "#f9f9f9",
            display: "flex",
            justifyContent: "center",
            borderTop: "1px solid #eee",
            zIndex: 10,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            variant="text"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionLogs;
